import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ArmrestPicker from '../components/ArmrestPicker';
import { RootState, AppDispatch } from '../store';
import {
	ArmrestsByCover,
	DeviceType,
	Lightbox,
	Platform,
	Settings,
	WriteDirection,
} from 'sofa-reborn-react';
import { setCurrentLightbox } from '../store/application/actions';
import { setSelectedArmrest } from '../store/armrest';
import { clearAdditionalCoverItems } from '../store/cover';
import { Product } from '@ikea-aoa/component-view-3d';
import { ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';

const ArmrestPickerContainer: React.FC = () => {
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const selectedArmrest = useSelector<RootState, Product | null>(
		(state) => state.armrest.selectedArmrest
	);
	const coverName = useSelector<RootState, string | undefined>(
		(state) => state.cover.selectedCover?.coverName
	);
	const armrestsByCover = useSelector<RootState, ArmrestsByCover | null>(
		(state) => state.armrest.armrests
	);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const armrests = armrestsByCover && coverName ? armrestsByCover[coverName] : null;
	const regularArmrest = armrests?.regularArmrest;
	const wideArmrest = armrests?.wideArmrest;
	let mounted = true;
	const dispatch = useDispatch<AppDispatch>();

	const [visible, setVisible] = useState(false);
	const [armrestData, setArmrestData] = useState<{
		regular: { measure: string | undefined; price: number | undefined };
		wide: { measure: string | undefined; price: number | undefined };
	} | null>(null);

	useEffect(() => {
		if (currentLightbox === 'armrest-picker') {
			if (!selectedArmrest) {
				dispatch(setSelectedArmrest(armrests?.regularArmrest, false));
			}
			setTimeout(() => {
				setVisible(true);
			}, 100);
		}
	}, [currentLightbox]);

	const setRegularArmrests = () => {
		if (selectedArmrest !== armrests?.regularArmrest) {
			dispatch(clearAdditionalCoverItems());
			dispatch(setSelectedArmrest(armrests?.regularArmrest, true));
		}
	};

	const setWideArmrests = () => {
		if (selectedArmrest !== armrests?.wideArmrest) {
			dispatch(clearAdditionalCoverItems());
			dispatch(setSelectedArmrest(armrests?.wideArmrest, true));
		}
	};

	async function setArmrestProductsMeasure() {
		if (armrests && kompisSettings) {
			const productService = new ProductService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});

			const regularArmrest = armrests.regularArmrest;
			const wideArmrest = armrests.wideArmrest;

			const regularArmrestProduct =
				regularArmrest?.type && regularArmrest?.productId
					? await productService.getProducts(
							[{ type: regularArmrest.type, id: regularArmrest.productId }],
							KompisDefaultFields
					  )
					: null;

			const wideArmrestProduct =
				wideArmrest?.type && wideArmrest?.productId
					? await productService.getProducts(
							[{ type: wideArmrest.type, id: wideArmrest.productId }],
							KompisDefaultFields
					  )
					: null;
			const useMetricMeasures = kompisSettings?.localisation.useMetricMeasures;

			const regular =
				regularArmrestProduct?.[0] &&
				(useMetricMeasures
					? regularArmrestProduct[0].content?.measure?.find(
							(measure) => measure.typeCode === '00047'
					  )?.textMetric
					: regularArmrestProduct[0].content?.measure?.find(
							(measure) => measure.typeCode === '00047'
					  )?.textImperial);
			const wide =
				wideArmrestProduct?.[0] &&
				(useMetricMeasures
					? wideArmrestProduct[0].content?.measure?.find((measure) => measure.typeCode === '00047')
							?.textMetric
					: wideArmrestProduct[0].content?.measure?.find((measure) => measure.typeCode === '00047')
							?.textImperial);

			const regularPrice =
				regularArmrestProduct?.[0] &&
				regularArmrestProduct[0].content?.priceInformation?.salesPrice[0].priceInclTax;
			const widePrice =
				wideArmrestProduct?.[0] &&
				wideArmrestProduct[0].content?.priceInformation?.salesPrice[0].priceInclTax;

			if (mounted && (wide || regular)) {
				if (widePrice && regularPrice && widePrice !== regularPrice) {
					setArmrestData({
						regular: { measure: regular, price: regularPrice },
						wide: { measure: wide, price: widePrice },
					});
				} else {
					setArmrestData({
						regular: { measure: regular, price: undefined },
						wide: { measure: wide, price: undefined },
					});
				}
			}
		}
	}

	useEffect(() => {
		if ((regularArmrest || wideArmrest) && kompisSettings) {
			setArmrestProductsMeasure();
		}
	}, [regularArmrest, wideArmrest, kompisSettings]);

	useEffect(() => {
		return (): void => {
			mounted = false;
		};
	}, []);

	return (
		<ArmrestPicker
			onClose={(): void => {
				setVisible(false);
				setTimeout(() => {
					dispatch(setCurrentLightbox('none'));
				}, 100);
			}}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			showSheet={visible}
			platform={platform}
			writeDirection={writeDirection}
			regularArmrest={armrests?.regularArmrest}
			wideArmrest={armrests?.wideArmrest}
			selectedArmrest={selectedArmrest}
			armrestData={armrestData}
			deviceType={deviceType}
			setRegularArmrests={setRegularArmrests}
			setWideArmrests={setWideArmrests}
		></ArmrestPicker>
	);
};

export default ArmrestPickerContainer;
